import React from "react";
import { Link } from "react-router-dom";
import "./MainContent.css";
import banner from '../../assets/images/IMG_1546_2.jpeg';
import profile from '../../assets/images/IMG_1546.jpeg';
import useScrollReveal from "../../hooks/useScrollReveal";

const scrollToContact = () => {
  const contactSection = document.getElementById("contact");
  if (contactSection) {
    contactSection.scrollIntoView({ behavior: "smooth" });
  }
};

/* Inline SVGs rather than an icon dependency — these are the only four. */
const icons = {
  roller: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="3" width="14" height="6" rx="1.5" />
      <path d="M17 6h2.5A1.5 1.5 0 0 1 21 7.5V10a1.5 1.5 0 0 1-1.5 1.5H12" />
      <path d="M12 11.5V14" />
      <rect x="9.5" y="14" width="5" height="7" rx="1.5" />
    </svg>
  ),
  brush: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L14 13l-4 1 1-4Z" />
      <path d="M9 14c-2 0-3 1-3 3 0 1.5-.8 2.4-2 3 1.2.7 2.6 1 4 1 2.8 0 4-1.8 4-4a3 3 0 0 0-3-3Z" />
    </svg>
  ),
  home: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 10.5 12 3l9 7.5" />
      <path d="M5 9.5V20a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V9.5" />
      <path d="M9.5 21v-6h5v6" />
    </svg>
  ),
  building: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="4" y="3" width="16" height="18" rx="1.5" />
      <path d="M8 7h2M14 7h2M8 11h2M14 11h2M8 15h2M14 15h2" />
      <path d="M10 21v-3h4v3" />
    </svg>
  ),
  check: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="m4 12.5 5 5L20 6.5" />
    </svg>
  ),
};

const services = [
  {
    icon: icons.roller,
    title: "Interior Painting",
    body: "Walls, ceilings, trim and doors — prepped properly and finished with smooth, even coats and clean lines.",
  },
  {
    icon: icons.brush,
    title: "Exterior Painting",
    body: "Siding, trim and accents refreshed with durable finishes built to hold up to Oregon weather.",
  },
  {
    icon: icons.home,
    title: "Residential",
    body: "From a single room to a whole-home repaint, we work around your schedule and leave the space clean.",
  },
  {
    icon: icons.building,
    title: "Commercial",
    body: "Storefronts, offices and units painted with minimal disruption to the way your business runs.",
  },
];

const trustPoints = [
  "Licensed & Insured",
  "CCB# 2446104",
  "Free Estimates",
  "Lebanon, Oregon",
];

export default function MainContent() {
  useScrollReveal();

  return (
    <main className="main-content">
      {/* ---------- Hero ---------- */}
      <section className="hero on-dark">
        <div
          className="hero-media"
          style={{ backgroundImage: `url(${banner})` }}
          role="img"
          aria-label="Freshly painted home by Pinto Painting"
        />
        <div className="hero-scrim" />

        <div className="container hero-inner">
          <p className="hero-promo">
            <span className="hero-promo-dot" />
            New customers get 20% off exterior jobs
          </p>

          <h1 className="hero-title">
            Welcome to <span className="hero-title-accent">Pinto Painting</span>
          </h1>

          <p className="hero-lead">
            Interior and exterior painting for homes and businesses across
            Lebanon, Oregon — precise prep, clean lines and finishes that last.
          </p>

          <div className="hero-actions">
            <button onClick={scrollToContact} className="btn btn-primary">
              Get a Free Estimate
            </button>
            <Link to="/projects" className="btn btn-ghost">
              View Our Work
            </Link>
          </div>
        </div>

        <div className="hero-fade" />
      </section>

      {/* ---------- Trust strip ---------- */}
      <section className="trust-strip">
        <div className="container trust-inner">
          {trustPoints.map(point => (
            <div className="trust-item" key={point}>
              <span className="trust-check">{icons.check}</span>
              {point}
            </div>
          ))}
        </div>
      </section>

      {/* ---------- Services ---------- */}
      <section className="section section-alt services">
        <div className="container">
          <header className="section-head" data-reveal>
            <p className="eyebrow">What We Do</p>
            <h2 className="section-title">Painting done properly</h2>
            <p className="section-lead">
              Every job gets the same treatment — thorough prep, quality
              materials, and attention to detail from the first coat to the last.
            </p>
          </header>

          <div className="service-grid">
            {services.map((service, i) => (
              <article
                className="service-card"
                key={service.title}
                data-reveal
                data-reveal-delay={i % 4}
              >
                <span className="service-icon">{service.icon}</span>
                <h3 className="service-title">{service.title}</h3>
                <p className="service-body">{service.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- About ---------- */}
      <section id="about" className="section about">
        <div className="container info-section">
          <div className="text-content" data-reveal>
            <p className="eyebrow">About Us</p>
            <h2 className="section-title">
              A local crew that treats your place like our own
            </h2>
            <p>
              At Pinto Painting, we take pride in delivering high-quality,
              reliable painting services that transform your space with
              precision and care.
            </p>
            <p>
              We specialize in both interior and exterior painting for
              residential and commercial properties. Whether it's refreshing a
              single room or giving your entire home a new look, our team
              ensures smooth finishes, clean lines, and long-lasting results.
            </p>
            <p>
              From prep work to the final coat, we handle every step with
              professionalism and attention to detail — making your vision a
              reality.
            </p>

            <button onClick={scrollToContact} className="btn btn-primary about-cta">
              Talk to Us
            </button>
          </div>

          <figure className="about-media" data-reveal data-reveal-delay="1">
            <img src={profile} alt="Pinto Painting at work" className="side-image" />
            <figcaption className="about-badge">
              <strong>CCB# 2446104</strong>
              <span>Licensed &amp; Insured</span>
            </figcaption>
          </figure>
        </div>
      </section>

      {/* ---------- Closing CTA ---------- */}
      <section className="cta-band on-dark">
        <div className="container cta-inner" data-reveal>
          <div>
            <h2 className="cta-title">Ready for a fresh coat?</h2>
            <p className="cta-sub">
              Tell us about your project and we'll put together a free, no
              pressure estimate.
            </p>
          </div>
          <button onClick={scrollToContact} className="btn btn-ghost cta-btn">
            Get a Free Estimate
          </button>
        </div>
      </section>
    </main>
  );
}
